import { c as _c } from "react/compiler-runtime";
import { LoadingSpinner, SegmentedControl } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import React, { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdown, useInterval, useTimeout } from 'usehooks-ts';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import { AUTOCAPTURE_RESTART_DELAY, AUTOCAPTURE_START_DELAY, AUTOCAPTURE_TIMER_INTERVAL, AUTOCAPTURE_TIMER_START_VAL, CAMERA_LOADING_FEEDBACK_DELAY, FEEDBACK_POSITION_FROM_BOTTOM_DESKTOP, FEEDBACK_POSITION_FROM_BOTTOM_MOBILE, FORCED_UPLOAD_DELAY, PLAY_CHECK_INTERVAL } from '../../constants';
import useHandleCameraError from '../../hooks/use-handle-camera-error';
import type { CameraErrorCause, CaptureKind, ReceivedImagePayload } from '../../types';
import { clearCanvas, getHtmlVideoElement, isDesktop, isDocument, isFace, isFunction, isMobile, isNonPlayingVideoEvent, isNotAllowedError, VideoEvents } from '../../utils/capture';
import getCameraErrorCause from '../../utils/get-camera-error-cause';
import CaptureButton from './components/capture-button';
import CountdownTimer from './components/countdown-timer';
import Feedback from './components/feedback';
import FocusSlider from './components/focus-slider';
import Overlay from './components/overlay';
import PlayPermissionDialog from './components/play-permission-dialog';
import UploadButton from './components/upload-button';
import useClipRecorder from './hooks/use-clip-recorder';
import useGetImageString from './hooks/use-get-image-string';
import useSize from './hooks/use-size';
import { getMediaStream } from './hooks/use-user-media';
import type { AutoCaptureKind, CaptureStatus, DeviceKind, Resolution, VideoRef } from './types';
import type { CameraSide } from './utils/get-camera-options';
import getCameraOptions from './utils/get-camera-options';
import getFocusStrategy, { type FocusRetryReason, readFocusStateWithRetry } from './utils/get-focus-strategy';
import getOutlineDimensions from './utils/get-outline-dimensions';
import { readPrefersManualMode, writePrefersManualMode } from './utils/manual-mode';
export type ChildrenProps = {
  canvasAutoCaptureRef: React.MutableRefObject<HTMLCanvasElement | undefined>;
  feedbackPositionFromBottom: number;
  videoResolution?: Resolution;
  onDetectionComplete: () => void;
  onDetectionReset: () => void;
  outlineHeight: number;
  outlineWidth: number;
  videoRef: VideoRef;
  videoSize: Resolution | undefined;
};
type CameraProps = {
  autocapture: boolean;
  autoCaptureFeedback?: CaptureStatus;
  autoCaptureKind: AutoCaptureKind;
  cameraSide: CameraSide;
  children: (props: ChildrenProps) => JSX.Element | null;
  deviceKind: DeviceKind;
  docName?: string;
  sideName?: string;
  onCapture: (image: string, captureKind: CaptureKind, video: Promise<Blob | undefined>) => void;
  onUpload: (payload: ReceivedImagePayload) => void;
  recordVideo?: boolean;
  hasBadConnectivity?: boolean;
  onCameraError?: (cause: CameraErrorCause) => void;
  onCameraAccessDenied?: () => void;
  outlineHeightRatio: number; // with respect to the video width (not height since width is smaller)
  outlineWidthRatio: number; // with respect to the video width
  isCaptured: boolean;
  setIsCaptured: React.Dispatch<React.SetStateAction<boolean>>;
  allowPdf: boolean;
  onCameraStuck: () => void;
  allowUpload: boolean;
};
const CountDownProps = {
  countStart: AUTOCAPTURE_TIMER_START_VAL,
  intervalMs: AUTOCAPTURE_TIMER_INTERVAL
};
const {
  logWarn,
  logInfo,
  logTrack
} = getLogger({
  name: 'idv2',
  location: 'camera'
});

// The DOM lib doesn't declare focusMode or focusDistance on the media track types, nor accept
// them as constraints; Chrome reads them from the advanced list.
type FocusTrackCapabilities = MediaTrackCapabilities & {
  focusMode?: string[];
  focusDistance?: {
    min: number;
    max: number;
    step: number;
  };
};
const CONTINUOUS_FOCUS_CONSTRAINTS = {
  advanced: [{
    focusMode: 'continuous'
  }]
} as unknown as MediaTrackConstraints;
const manualFocusConstraints = (focusDistance: number): MediaTrackConstraints => ({
  advanced: [{
    focusMode: 'manual',
    focusDistance
  }]
}) as unknown as MediaTrackConstraints;
const asJsonLabel = (value: unknown): string => value === undefined ? 'undefined' : JSON.stringify(value);
const getFocusDistanceControls = (capabilities: FocusTrackCapabilities) => {
  const min = capabilities.focusDistance?.min ?? 0;
  return {
    min,
    max: capabilities.focusDistance?.max ?? 0,
    step: capabilities.focusDistance?.step ?? 1,
    current: min
  };
};

// Accumulates a mount's successful lifecycle steps for the single report on unmount. Failure
// paths deliberately stay out of it — they log as they happen, at their own severity.
type CameraSession = {
  startedAt: number;
  videoEvents: Set<string>;
  focus: string;
  focusCapabilities: string;
  focusSettings: string;
  supportsFocusDistance: boolean;
  focusSliderUsed: boolean;
  focusApplyFailed: boolean;
  focusCapsRetryReason?: FocusRetryReason;
  srcObjectSet: boolean;
  canPlayTriggered: boolean;
  reachedPlaying: boolean;
};
type VideoElementStateListenerProps = {
  setPlayingState: (state: boolean) => void;
  onPlayNotAllowed: () => void;
  videoElement: HTMLVideoElement;
  isFirstTime: (key: string) => boolean;
  session: CameraSession;
};
const videoElementStateListener = ({
  setPlayingState,
  onPlayNotAllowed,
  videoElement,
  isFirstTime,
  session
}: VideoElementStateListenerProps) => (event: Event) => {
  if (!videoElement) {
    return;
  }
  session.videoEvents.add(event.type);
  if (isFunction(videoElement?.play) && isNonPlayingVideoEvent(event) && videoElement.readyState >= 2) {
    setPlayingState(false);
    videoElement.play().then(() => {
      session.reachedPlaying = true;
      setPlayingState(true);
    }).catch(err => {
      if (isNotAllowedError(err.name)) {
        onPlayNotAllowed();
        if (isFirstTime('video-event:play-not-allowed')) {
          logWarn('Video play event: not allowed - prompting user interaction', err);
        }
      } else if (isFirstTime('video-event:play-error')) {
        logWarn('Video play event: error', err);
      }
    });
  }
};
const getPositionFromBottom = (kind: DeviceKind): 200 | 50 => kind === 'mobile' ? FEEDBACK_POSITION_FROM_BOTTOM_MOBILE : FEEDBACK_POSITION_FROM_BOTTOM_DESKTOP;
const getPositionFromTop = (kind: DeviceKind, size?: Resolution): number => kind === 'mobile' ? (size?.height ?? 0) - FEEDBACK_POSITION_FROM_BOTTOM_MOBILE : (size?.height ?? 0) - FEEDBACK_POSITION_FROM_BOTTOM_DESKTOP;
const getDesiredSize = (videoSize: Resolution, {
  autoCaptureKind,
  positionFromBottom,
  outlineHeightRatio,
  outlineWidthRatio
}: {
  autoCaptureKind: AutoCaptureKind;
  positionFromBottom: number;
  outlineHeightRatio: number;
  outlineWidthRatio: number;
}) => {
  // For document capture, we keep the captured width/height spect ratio same as the outline aspect ratio
  // In case maintaining the aspect ratio overflows the height, we take the full height (Math.min)
  if (isDocument(autoCaptureKind)) {
    return {
      width: videoSize.width,
      height: Math.min(videoSize.height - positionFromBottom, videoSize.width * (outlineHeightRatio / outlineWidthRatio))
    };
  }
  return videoSize; // We capture the full height for selfie
};
const getVideoTrackSettings = (stream?: MediaStream | null): MediaTrackSettings | undefined => {
  if (!stream) {
    return undefined;
  }
  const videoTracks = stream.getVideoTracks();
  if (videoTracks.length === 0) {
    return undefined;
  }
  const videoTrack = videoTracks[0];
  if (!videoTrack) {
    return undefined;
  }
  return videoTrack.getSettings();
};
const getVideoResolution = (stream?: MediaStream | null): Resolution | undefined => {
  const settings = getVideoTrackSettings(stream);
  return settings?.width && settings.height ? {
    width: settings.width,
    height: settings.height
  } : undefined;
};
const Camera = ({
  autocapture,
  autoCaptureFeedback,
  autoCaptureKind,
  cameraSide,
  children,
  deviceKind,
  docName,
  sideName,
  onCapture,
  onUpload,
  recordVideo = false,
  onCameraError,
  onCameraAccessDenied,
  outlineHeightRatio,
  outlineWidthRatio,
  isCaptured,
  setIsCaptured,
  allowPdf,
  onCameraStuck,
  allowUpload,
  hasBadConnectivity
}: CameraProps) => {
  const {
    t
  } = useTranslation('idv2', {
    keyPrefix: 'document-flow.components.camera'
  });
  const canvasAutoCaptureRef = useRef<HTMLCanvasElement>(undefined);
  const canvasImageCaptureRef = useRef<HTMLCanvasElement>(undefined);
  const videoRef: VideoRef = useRef<HTMLVideoElement>(null);
  const autocaptureRestartTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Media events and the play-check interval repeat for as long as the camera misbehaves, so
  // every condition below reports its first occurrence per mount rather than each repeat.
  const loggedOnceRef = useRef<Set<string>>(new Set());
  const isFirstTime = (key: string) => {
    if (loggedOnceRef.current.has(key)) {
      return false;
    }
    loggedOnceRef.current.add(key);
    return true;
  };
  const sessionRef = useRef<CameraSession>({
    startedAt: Date.now(),
    videoEvents: new Set<string>(),
    focus: 'not-reached',
    focusCapabilities: '',
    focusSettings: '',
    supportsFocusDistance: false,
    focusSliderUsed: false,
    focusApplyFailed: false,
    srcObjectSet: false,
    canPlayTriggered: false,
    reachedPlaying: false
  });
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [prefersManualMode, setPrefersManualMode] = useState(readPrefersManualMode);
  const isManualMode = !autocapture || prefersManualMode;
  const [showPlayAllowDialog, setShowPlayAllowDialog] = useState(false);
  const [onCanPlayTriggered, setOnCanPlayTriggered] = useState(false);
  const [showCameraLoadingFeedback, setShowCameraLoadingFeedback] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const {
    takeClip
  } = useClipRecorder(recordVideo ? mediaStream : null, isVideoPlaying && !isCaptured);
  const [manualFocusDistanceControls, setManualFocusDistanceControls] = useState<{
    min: number;
    max: number;
    step: number;
    current: number;
  } | null>(null);
  const onCameraErrorToast = useHandleCameraError();
  const showManualFocusDistanceControls = manualFocusDistanceControls !== null && manualFocusDistanceControls.max > manualFocusDistanceControls.min && manualFocusDistanceControls.step > 0;

  // Callback refs keep latest prop values accessible inside effects without
  // adding them to the dep array (which would cause the stream to re-init).
  const onCameraErrorRef = useRef(onCameraError);
  onCameraErrorRef.current = onCameraError;
  const onCameraAccessDeniedRef = useRef(onCameraAccessDenied);
  onCameraAccessDeniedRef.current = onCameraAccessDenied;
  const onCameraErrorToastRef = useRef(onCameraErrorToast);
  onCameraErrorToastRef.current = onCameraErrorToast;
  useEffect(() => {
    trackAction('camera-page:started');
  }, []);
  useEffect(() => {
    if (isVideoPlaying) {
      trackAction('camera-page:video-playing');
    }
  }, [isVideoPlaying]);
  const videoSize = useSize(videoRef);
  const [autoCaptureTimerVal, {
    startCountdown,
    stopCountdown,
    resetCountdown
  }] = useCountdown(CountDownProps);
  const getImageStringFromVideo = useGetImageString();
  const focusManually = async (track: MediaStreamTrack, distance: number): Promise<boolean> => {
    try {
      await track.applyConstraints(manualFocusConstraints(distance));
      return true;
    } catch (err) {
      sessionRef.current.focusApplyFailed = true;
      logWarn('(focusManually) Error focusing manually', err);
      return false;
    }
  };
  const adjustFocusMode = async (track_0: MediaStreamTrack, capabilities: MediaTrackCapabilities, settings: MediaTrackSettings) => {
    const strategy = getFocusStrategy(capabilities, settings);
    const session = sessionRef.current;
    if (strategy.kind === 'no-focus-capability') {
      session.focus = 'unsupported';
      return;
    }
    const focusCaps = capabilities as FocusTrackCapabilities;
    session.focusCapabilities = asJsonLabel(focusCaps.focusMode);
    session.focusSettings = asJsonLabel((settings as MediaTrackSettings & {
      focusMode?: string;
    }).focusMode);
    session.supportsFocusDistance = focusCaps.focusDistance !== undefined;
    try {
      if (strategy.kind === 'continuous') {
        session.focus = 'continuous';
        await track_0.applyConstraints(CONTINUOUS_FOCUS_CONSTRAINTS);
      } else if (strategy.kind === 'manual') {
        session.focus = 'manual';
        const controls = getFocusDistanceControls(focusCaps);
        const applied = await focusManually(track_0, controls.current);
        if (!applied) {
          session.focus = 'manual-apply-failed';
        }
        setManualFocusDistanceControls(controls);
      } else {
        // 'manual-despite-settings' lands here too: the driver claims continuous focus while
        // advertising manual as its only mode, and until telemetry says which reading is honest,
        // acting on either is a guess.
        session.focus = strategy.kind;
      }
    } catch (err_0) {
      session.focus = 'failed';
      logWarn('(adjustFocusMode) Error changing focus mode', err_0);
    }
  };

  // Capability reads are only trustworthy once playback has started.
  const focusAdjustedTrackRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mediaStream || !isVideoPlaying) {
      return undefined;
    }
    const track_1 = mediaStream.getVideoTracks().length > 0 ? mediaStream.getVideoTracks()[0] : null;
    if (!track_1 || focusAdjustedTrackRef.current === track_1.id) {
      return undefined;
    }
    return readFocusStateWithRetry(track_1, {
      onRetry: reason => {
        sessionRef.current.focusCapsRetryReason = reason;
      },
      onSettled: (capabilities_0, settings_0) => {
        focusAdjustedTrackRef.current = track_1.id;
        adjustFocusMode(track_1, capabilities_0, settings_0);
      }
    });
  }, [mediaStream, isVideoPlaying]);
  useEffect(() => {
    let stale = false;
    if (!mediaStream?.active) {
      const cameraOptions = getCameraOptions(cameraSide);
      getMediaStream(cameraOptions).then(stream => {
        if (stale) {
          stream.getTracks().forEach(track_2 => {
            track_2.stop();
          });
        } else {
          setMediaStream(stream);
        }
      }).catch(err_1 => {
        if (!stale) {
          logWarn('(camera useEffect) Error getting media stream', err_1);
          onCameraErrorToastRef.current(err_1);
          const cause = getCameraErrorCause(err_1);
          if (cause === 'permission-denied') {
            onCameraAccessDeniedRef.current?.();
          } else {
            onCameraErrorRef.current?.(cause);
          }
        }
      });
    }
    return () => {
      stale = true;
      if (mediaStream) {
        mediaStream.getTracks().forEach(track_3 => {
          track_3.stop();
        });
      }
    };
  }, [mediaStream, mediaStream?.active]);
  useEffect(() => {
    if (mediaStream && videoRef.current && !videoRef.current.srcObject) {
      sessionRef.current.srcObjectSet = true;
      videoRef.current.srcObject = mediaStream;
    }
  }, [mediaStream]);
  const isCameraVisible = Boolean(mediaStream?.active) && isVideoPlaying;
  const {
    outlineWidth,
    outlineHeight
  } = getOutlineDimensions({
    videoSize,
    outlineHeightRatio,
    outlineWidthRatio,
    deviceKind
  });
  const positionFromTop = getPositionFromTop(deviceKind, videoSize);
  const positionFromBottom = getPositionFromBottom(deviceKind);
  const debouncedFocusManually = useMemo(() => debounce(focusManually, 100), []);
  const handleFocusChange = async (value: number) => {
    if (!mediaStream) {
      return;
    }
    const track_4 = mediaStream.getVideoTracks().length > 0 ? mediaStream.getVideoTracks()[0] : null;
    if (!track_4) {
      return;
    }
    sessionRef.current.focusSliderUsed = true;
    setManualFocusDistanceControls(prev => prev ? {
      ...prev,
      current: value
    } : null);
    await debouncedFocusManually(track_4, value);
  };
  const handlePlayError = (err_2: unknown) => {
    if (err_2 instanceof Error && isNotAllowedError(err_2?.name)) {
      if (!showPlayAllowDialog) {
        setShowPlayAllowDialog(true);
      }
      if (isFirstTime('play:not-allowed')) {
        logWarn('video play: not allowed - prompting user interaction', err_2);
      }
    } else if (isFirstTime('play:error')) {
      logWarn('video play: error', err_2);
    }
  };
  const handleCanPlay = () => {
    setOnCanPlayTriggered(true);
    sessionRef.current.canPlayTriggered = true;
    if (isVideoPlaying) {
      return;
    }
    if (!videoRef.current) {
      if (isFirstTime('canplay:no-video-ref')) {
        logWarn('(onCanPlay) video ref not initialized');
      }
      return;
    }
    if (videoRef.current.readyState < 2) {
      if (isFirstTime('canplay:not-ready')) {
        logWarn('(onCanPlay) video not ready to play');
      }
      return;
    }
    const playPromise = videoRef.current.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        sessionRef.current.reachedPlaying = true;
        setIsVideoPlaying(true);
      }).catch(handlePlayError);
    }
  };
  const logMediaStreamState = (reason_0: string) => {
    // Alerting queries the reason, so it goes out at warn; the per-track detail is only ever
    // read while investigating one.
    logWarn(`${reason_0}. mediaStream state: ${mediaStream ? 1 : 0}, mediaStream active: ${mediaStream?.active ? 1 : 0}`);
    mediaStream?.getTracks().forEach(track_5 => {
      logInfo(`Track: ${track_5.id}, readyState: ${track_5.readyState}, kind: ${track_5.kind}, label: ${track_5.label}`);
    });
  };
  const handleWaiting = () => {
    // `waiting` repeats on a struggling stream, and the load() below re-emits it.
    if (isFirstTime('video-waiting')) {
      logMediaStreamState('video waiting');
    }

    // Force load
    // TODO: if this doesn't work, we will re-create the media stream - try a simple reload first
    videoRef.current?.load();
    if (isVideoPlaying) {
      setIsVideoPlaying(false);
    }
  };
  const handleSuspension = () => {
    if (isFirstTime('video-suspended')) {
      logMediaStreamState('video suspended');
    }
  };
  useInterval(() => {
    if (isVideoPlaying) {
      return;
    }
    if (!videoRef.current) {
      if (isFirstTime('interval:no-video-ref')) {
        logWarn('(interval) video ref not initialized');
      }
      return;
    }
    if (!videoRef.current.srcObject) {
      if (isFirstTime('interval:no-src-object')) {
        logWarn(`(interval) video src object not set. mediaStream state: ${mediaStream ? 1 : 0}, mediaStream active: ${mediaStream?.active ? 1 : 0}, onCanPlayTriggered: ${onCanPlayTriggered ? 1 : 0}`);
      }
      if (mediaStream?.active) {
        videoRef.current.srcObject = mediaStream;
      }
      return;
    }
    if (videoRef.current.readyState < 2) {
      if (isFirstTime(`interval:readystate-${videoRef.current.readyState}`)) {
        logWarn(`(interval) video not ready to play. Readystate: ${videoRef.current.readyState}`);
      }
      return;
    }
    const playPromise_0 = videoRef.current.play();
    if (playPromise_0 !== undefined) {
      playPromise_0.then(() => {
        logWarn('(interval) video element status: started playing');
        setIsVideoPlaying(true);
      }).catch(handlePlayError);
    }
  }, isVideoPlaying ? null : PLAY_CHECK_INTERVAL);
  useTimeout(() => {
    if (!isCameraVisible) {
      logWarn('camera not visible after timeout');
      onCameraStuck();
    }
  }, FORCED_UPLOAD_DELAY);
  useTimeout(() => {
    setShowCameraLoadingFeedback(true);
  }, CAMERA_LOADING_FEEDBACK_DELAY);
  const handlePlayAllow = () => {
    setShowPlayAllowDialog(false);
    handleCanPlay();
  };
  const handleClick = useCallback(async (captureKind: CaptureKind) => {
    logTrack(`(handleClick) ${captureKind} clicked`);
    if (!canvasImageCaptureRef.current || !videoRef.current) {
      logWarn(`Video ref or canvas not initialized for camera capture for capture kind: ${captureKind}`);
      return;
    }
    const context = canvasImageCaptureRef.current.getContext('2d');
    if (!context) {
      logWarn(`Canvas context is undefined for camera capture for capture kind: ${captureKind}`);
      return;
    }
    if (!videoSize) {
      logWarn(`Cannot capture - videoSize not initialized: ${captureKind}`);
      return;
    }
    const desiredSize = getDesiredSize(videoSize, {
      autoCaptureKind,
      positionFromBottom,
      outlineHeightRatio,
      outlineWidthRatio
    });
    const videoResolution = getVideoResolution(mediaStream);
    const imageString = await getImageStringFromVideo({
      autoCaptureKind,
      canvasRef: canvasImageCaptureRef,
      centerOffsetY: isDocument(autoCaptureKind) ? -positionFromBottom / 2 : 0,
      context,
      desiredImageHeight: desiredSize.height,
      desiredImageWidth: desiredSize.width,
      videoResolution,
      videoRef
    });
    if (imageString) {
      logInfo(`handleClick: captured ${captureKind} image, ${imageString.length} chars at ${desiredSize.width}x${desiredSize.height}`, {
        videoWidth: videoResolution?.width,
        videoHeight: videoResolution?.height
      });
      // Taken before the capture is committed, which stops the recorders.
      const video = takeClip();
      // Stop any running auto-capture countdown before committing the capture
      stopCountdown();
      setIsTimerRunning(false);
      resetCountdown();
      setIsCaptured(true);
      onCapture(imageString, captureKind, video);
    } else {
      logWarn(`handleClick: no image produced for capture kind: ${captureKind}`);
    }
    clearCanvas(logWarn, canvasImageCaptureRef);
  }, [autoCaptureKind, positionFromBottom, outlineHeightRatio, outlineWidthRatio, videoSize, mediaStream, getImageStringFromVideo, onCapture, setIsCaptured, stopCountdown, resetCountdown, takeClip]);
  const onImageUpload = () => {
    setIsImageProcessing(true);
    setIsDetecting(false);
  };
  const onUploadComplete = () => {
    setIsImageProcessing(false);
    setIsDetecting(true);
  };
  const onMobileCaptureClick = () => {
    if (isTimerRunning) {
      handleResetDetectionTimer();
      setIsDetecting(false); // We can cancel the countdown
      const restartTimeout = setTimeout(() => setIsDetecting(true), AUTOCAPTURE_RESTART_DELAY); // We wait 1s before re-detecting and starting the countdown again
      autocaptureRestartTimeoutRef.current = restartTimeout;
    } else {
      setIsDetecting(false); // Stop detection
      clearTimeout(autocaptureRestartTimeoutRef.current); // Clear any pending detection restarts

      // Small timeout to ensure face detection has completely stopped
      // and any pending face-api operations are cleared
      setTimeout(() => {
        handleClick('manual');
      }, 0);
    }
  };
  const handleResetDetectionTimer = () => {
    resetCountdown();
    setIsTimerRunning(false);
  };
  const handleDetectionComplete = () => {
    setIsTimerRunning(true);
    startCountdown();
  };
  const prevIsCapturedRef = useRef(false);
  useEffect(() => {
    if (prevIsCapturedRef.current && !isCaptured) {
      resetCountdown();
      setIsTimerRunning(false);
      setIsDetecting(false);
      clearTimeout(autocaptureRestartTimeoutRef.current);
      if (!isManualMode) {
        const timeout = setTimeout(() => setIsDetecting(true), AUTOCAPTURE_START_DELAY);
        autocaptureRestartTimeoutRef.current = timeout;
      }
    }
    prevIsCapturedRef.current = isCaptured;
  }, [isCaptured]); // eslint-disable-line react-hooks/exhaustive-deps

  // We don't detect for 3 seconds so the camera has time to focus
  useTimeout(() => {
    if (!isManualMode) {
      setIsDetecting(true);
    }
  }, isCameraVisible ? AUTOCAPTURE_START_DELAY : null);
  const handleToggleCaptureMode = (toManual: boolean) => {
    writePrefersManualMode(toManual);
    if (toManual) {
      setPrefersManualMode(true);
      setIsDetecting(false);
      resetCountdown();
      setIsTimerRunning(false);
      clearTimeout(autocaptureRestartTimeoutRef.current);
    } else {
      setPrefersManualMode(false);
      clearTimeout(autocaptureRestartTimeoutRef.current);
      const restartTimeout_0 = setTimeout(() => setIsDetecting(true), AUTOCAPTURE_START_DELAY);
      autocaptureRestartTimeoutRef.current = restartTimeout_0;
    }
  };
  useEffect(() => {
    if (autoCaptureTimerVal <= 0 && isTimerRunning && !isCaptured) {
      stopCountdown();
      handleClick('auto');
    }
  }, [autoCaptureTimerVal, isTimerRunning, isCaptured, stopCountdown, handleClick]);
  useEffect(() => {
    const vidRef = getHtmlVideoElement(videoRef);
    if (!vidRef || !isVideoPlaying) {
      return noop;
    }
    const listener = videoElementStateListener({
      setPlayingState: setIsVideoPlaying,
      onPlayNotAllowed: () => setShowPlayAllowDialog(true),
      videoElement: vidRef,
      isFirstTime,
      session: sessionRef.current
    });
    VideoEvents.forEach(e => {
      vidRef.addEventListener(e, listener);
    });
    return () => {
      VideoEvents.forEach(e_0 => {
        vidRef.removeEventListener(e_0, listener);
      });
    };
  }, [isVideoPlaying, videoRef]);
  useEffect(() => () => clearTimeout(autocaptureRestartTimeoutRef.current), []);
  useEffect(() => {
    const session_0 = sessionRef.current;
    return () => {
      logInfo('camera session', {
        autoCaptureKind,
        cameraSide,
        deviceKind,
        durationMs: Date.now() - session_0.startedAt,
        videoEvents: Array.from(session_0.videoEvents).join(','),
        focus: session_0.focus,
        focusCapabilities: session_0.focusCapabilities || undefined,
        focusSettings: session_0.focusSettings || undefined,
        supportsFocusDistance: session_0.supportsFocusDistance,
        focusSliderUsed: session_0.focusSliderUsed,
        focusApplyFailed: session_0.focusApplyFailed,
        focusCapsRetryReason: session_0.focusCapsRetryReason,
        srcObjectSet: session_0.srcObjectSet,
        canPlayTriggered: session_0.canPlayTriggered,
        reachedPlaying: session_0.reachedPlaying
      });
    };
  }, []);
  const feedbackKey = `autocapture.feedback.${autoCaptureKind}.${isManualMode ? 'detecting' : autoCaptureFeedback}`;
  // @ts-ignore -- dynamic key not supported by typed t()
  const feedbackText = t(feedbackKey, {
    side: sideName ?? '',
    documentType: docName ?? ''
  }) as unknown as string;
  return <>
      <div className="flex items-center flex-col justify-between w-full h-full gap-y-6">
        <div className={cx('relative flex items-center justify-start flex-grow flex-col', deviceKind === 'desktop' && 'w-full max-md:px-2', deviceKind === 'mobile' && 'w-[calc(100%+32px)] -m-4 md:justify-end md:h-[530px]')} data-device-kind={deviceKind}>
          <video autoPlay data-camera-kind={cameraSide} data-device-kind={deviceKind} hidden={!isVideoPlaying} muted onCanPlay={handleCanPlay} playsInline ref={videoRef} onSuspend={handleSuspension} onWaiting={handleWaiting} className={cx('h-full w-full object-cover', cameraSide === 'front' && '-scale-x-100', deviceKind === 'desktop' && 'rounded', '[&::-webkit-media-controls-play-button]:hidden [&::-webkit-media-controls-play-button]:appearance-none')} />
          {isVideoPlaying && isDetecting ? children({
          canvasAutoCaptureRef,
          feedbackPositionFromBottom: positionFromBottom,
          videoResolution: getVideoResolution(mediaStream),
          onDetectionComplete: handleDetectionComplete,
          onDetectionReset: handleResetDetectionTimer,
          outlineHeight,
          outlineWidth,
          videoRef,
          videoSize
        }) : null}
          {isCameraVisible && (isImageProcessing ? <div className="absolute inset-0 opacity-75 flex justify-center items-center bg-primary">
                <LoadingSpinner />
              </div> : <>
                {!isCaptured && <Overlay width={videoSize?.width ?? 0} height={positionFromTop} videoHeight={videoSize?.height ?? 0} captureKind={autoCaptureKind} outlineWidth={outlineWidth} outlineHeight={outlineHeight} timerAnimationVal={isTimerRunning ? autoCaptureTimerVal : undefined} />}
                <canvas ref={canvasImageCaptureRef as React.Ref<HTMLCanvasElement>} width={videoSize?.width} height={videoSize?.height} className="invisible absolute" />
                <canvas ref={canvasAutoCaptureRef as React.Ref<HTMLCanvasElement>} width={videoSize?.width} height={videoSize?.height} className="invisible absolute" />
                {showManualFocusDistanceControls && <div className="absolute left-8 right-8 flex justify-center gap-2 items-center" style={{
            top: positionFromTop - 48
          }}>
                    <p className="text-caption-1 text-quinary">{t($ => $.focus)}</p>
                    <FocusSlider min={manualFocusDistanceControls?.min ?? 0} max={manualFocusDistanceControls?.max ?? 0} step={manualFocusDistanceControls?.step ?? 1} current={manualFocusDistanceControls?.current ?? 0} onChange={handleFocusChange} />
                  </div>}
                {(isManualMode ? isDocument(autoCaptureKind) || isFace(autoCaptureKind) : !!autoCaptureFeedback && (isMobile(deviceKind) || autoCaptureFeedback !== 'detecting' || isFace(autoCaptureKind))) ? <Feedback deviceKind={deviceKind} top={positionFromTop}>
                    {feedbackText}
                  </Feedback> : null}
                {isMobile(deviceKind) && autocapture && <div className="absolute bottom-[100px] left-0 right-0 flex justify-center">
                    <SegmentedControl aria-label={t($_0 => $_0.captureMode.label)} value={isManualMode ? 'manual' : 'auto'} onChange={val => handleToggleCaptureMode(val === 'manual')} options={[{
              value: 'auto',
              label: t($_1 => $_1.captureMode.auto)
            }, {
              value: 'manual',
              label: t($_2 => $_2.captureMode.manual)
            }]} />
                  </div>}
                {isMobile(deviceKind) && <CaptureButton onClick={onMobileCaptureClick} variant={isTimerRunning ? 'stop' : 'round'} disabled={!isCameraVisible || !videoSize} />}
                {isDocument(autoCaptureKind) && allowUpload && <UploadButton onUploadBtnClick={onImageUpload} onUploadChangeDone={onUploadComplete} allowPdf={allowPdf} onUploadSuccess={onUpload} hasBadConnectivity={hasBadConnectivity} />}
                {isTimerRunning ? <div className="w-full flex justify-center items-center absolute border-none bg-transparent" style={{
            height: `${positionFromTop}px`
          }}>
                    <CountdownTimer current={autoCaptureTimerVal} start={AUTOCAPTURE_TIMER_START_VAL} />
                  </div> : null}
              </>)}
        </div>
        {isDesktop(deviceKind) && isCameraVisible && <div className="flex flex-col items-center gap-4 w-full">
            {autocapture && <SegmentedControl aria-label={t($_3 => $_3.captureMode.label)} value={isManualMode ? 'manual' : 'auto'} onChange={val_0 => handleToggleCaptureMode(val_0 === 'manual')} options={[{
          value: 'auto',
          label: t($_4 => $_4.captureMode.auto)
        }, {
          value: 'manual',
          label: t($_5 => $_5.captureMode.manual)
        }]} />}
            <CaptureButton onClick={() => handleClick('manual')} disabled={!isCameraVisible || !videoSize || isCaptured} variant="default" />
          </div>}
        {/* Must render even when isCameraVisible is false: this is the recovery for
            WKWebView blocking video.play() without a user gesture, which is precisely
            when isVideoPlaying (and so isCameraVisible) are false. */}
        <PlayPermissionDialog open={showPlayAllowDialog} hide={() => setShowPlayAllowDialog(false)} onAllow={handlePlayAllow} />
      </div>
      {!isCameraVisible && <div className={cx('flex justify-center items-center flex-col gap-6', deviceKind === 'mobile' && 'absolute -inset-4 bg-primary', deviceKind === 'desktop' && 'relative flex-1 w-full h-full bg-secondary rounded max-md:px-2 min-h-[280px]')} data-device-kind={deviceKind}>
          <AnimatePresence>
            <LoadingSpinner size="xlarge" />
            {showCameraLoadingFeedback && <motion.div className="flex flex-col gap-2 h-fit" animate={{
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.5
          }
        }} initial={{
          opacity: 0,
          y: 10
        }} exit={{
          opacity: 0,
          y: 10
        }}>
                <p className="text-label-1 text-primary fp-text">{t($_6 => $_6.loading.title)}</p>
                <p className="text-body-2 text-secondary fp-text">{t($_7 => $_7.loading.subtitle)}</p>
              </motion.div>}
          </AnimatePresence>
        </div>}
    </>;
};
export const Container = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("flex items-center flex-col justify-between w-full h-full gap-y-6", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const Canvas = React.forwardRef<HTMLCanvasElement, React.CanvasHTMLAttributes<HTMLCanvasElement>>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <canvas ref={ref} className="invisible absolute" {...props} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
export default React.memo(Camera);